import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MdMenu, MdClose } from 'react-icons/md';
import { useAppSelector } from '../../store/hooks';

interface PublicHeaderProps {
  /** Optional content to render left of logo (e.g. back button) */
  leftContent?: React.ReactNode;
}

/**
 * Shared header for public pages: Home, Blog, About Us.
 * Tools link is in footer only. Consistent nav tabs and auth buttons across all tabs.
 */
const PublicHeader: React.FC<PublicHeaderProps> = ({ leftContent }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAppSelector((state) => state.user);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const isHome = location.pathname === '/' || location.pathname === '';
  const isBlog = location.pathname.startsWith('/blog');
  const isAbout = location.pathname.startsWith('/about');

  const goToDashboard = () => navigate('/dashboard');

  const navTab = (path: string, label: string, active: boolean) => (
    <button
      key={path}
      onClick={() => navigate(path)}
      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
        active ? 'text-orange-600 bg-orange-50 shadow-sm' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
      }`}
    >
      {label}
    </button>
  );

  const NavDivider = () => <div className="w-px h-4 bg-orange-200/50" />;

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 border-b border-orange-100 bg-[#FFF8F0]/80 backdrop-blur-sm"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo + optional left content */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {leftContent}
            <Link to="/" className="flex items-center" aria-label="Focuzon Home">
              <img
                src="/focuzon.png"
                alt="Focuzon"
                className="h-8 w-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
                style={{ maxWidth: '140px', minWidth: '100px' }}
              />
            </Link>
          </div>

          {/* Nav tabs - centered */}
          <div className="hidden lg:flex items-center justify-center flex-1">
            <div className="flex items-center space-x-1 bg-white/60 backdrop-blur-sm rounded-full px-2 py-1.5 border border-orange-100/50 shadow-sm">
              {navTab('/', 'Home', isHome)}
              <NavDivider />
              {navTab('/blog', 'Blog', isBlog)}
              <NavDivider />
              {navTab('/about', 'About Us', isAbout)}
            </div>
          </div>

          {/* Auth buttons - right */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            {!isAuthenticated ? (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="hidden sm:block text-gray-700 hover:text-orange-600 font-medium transition-colors text-sm px-3 py-1.5 rounded-lg hover:bg-orange-50/50"
                >
                  Log in
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="px-4 py-2 rounded-lg text-orange-600 font-medium text-sm whitespace-nowrap border border-orange-300 bg-white/80 hover:bg-orange-50 transition-colors"
                >
                  Try Focuzon Free
                </button>
              </>
            ) : (
              <button
                onClick={goToDashboard}
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-200 text-sm whitespace-nowrap"
              >
                Go to Dashboard
              </button>
            )}

            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden text-gray-700 hover:text-orange-600 transition-colors p-2 rounded-lg hover:bg-orange-50/50"
              aria-label="Toggle menu"
            >
              {showMobileMenu ? <MdClose className="w-6 h-6" /> : <MdMenu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {showMobileMenu && (
          <div className="lg:hidden border-t border-orange-100 py-4 bg-white/60 backdrop-blur-sm">
            <div className="flex flex-col space-y-2 px-2">
              {[
                { path: '/', label: 'Home', active: isHome },
                { path: '/blog', label: 'Blog', active: isBlog },
                { path: '/about', label: 'About Us', active: isAbout },
              ].map(({ path, label, active }) => (
                <button
                  key={path}
                  onClick={() => {
                    navigate(path);
                    setShowMobileMenu(false);
                  }}
                  className={`text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    active ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default PublicHeader;
